import { motion } from 'framer-motion'
import { ReactNode } from 'react'
import tw, { TwStyle } from 'twin.macro'

type ToggleProps = {
  toggleHandler: () => void
  isOn: boolean
  icons: Array<ReactNode> | null
  twStyle?: TwStyle
}

const spring = {
  type: 'spring',
  stiffness: 700,
  damping: 30
}

const styles = {
  container: ({ isOn, twStyle }: { isOn: boolean; twStyle?: TwStyle }) => [
    tw`flex items-center bg-[rgba(0, 0, 0, 0.04)] px-[4px] py-[2px] rounded-full w-[4.125rem] h-8 relative `,
    isOn ? tw`justify-end` : tw`justify-start`,
    twStyle
  ],
  toggle: [tw`w-7 h-7 bg-primary-middle rounded-full p-1 text-white flex justify-center items-center`],
  right: ({ isOn }: { isOn: boolean }) => [tw`absolute right-2 text-primary`, isOn && tw`hidden`],
  left: ({ isOn }: { isOn: boolean }) => [tw`absolute left-2 text-primary`, !isOn && tw`hidden`],
  dot: ({ isOn, isLeft }: { isOn: boolean; isLeft: boolean }) => [
    tw`rounded-full w-4 h-4 bg-white`,
    !isOn && !isLeft && tw`bg-gray-300`,
    isOn && isLeft && tw`bg-gray-400`
  ]
}

const RightIcon = ({ isOn, icons }: { isOn: boolean; icons: Array<ReactNode> | null }) => {
  if (icons) {
    return icons.length ? (icons[0] as JSX.Element) : null
  }
  return <div css={styles.dot({ isOn, isLeft: false })} />
}

const LeftIcon = ({ isOn, icons }: { isOn: boolean; icons: Array<ReactNode> | null }) => {
  if (icons) {
    return icons.length ? (icons[1] as JSX.Element) : null
  }
  return <div css={styles.dot({ isOn, isLeft: true })} />
}

const Toggle = ({ icons, toggleHandler, isOn, twStyle }: ToggleProps) => {
  return (
    <div aria-hidden={true} css={styles.container({ isOn, twStyle })} onClick={toggleHandler}>
      <motion.div css={styles.toggle} layout transition={spring}>
        {isOn ? <RightIcon isOn={isOn} icons={icons} /> : <LeftIcon isOn={isOn} icons={icons} />}
      </motion.div>
      <span css={styles.right({ isOn })}>
        <RightIcon isOn={isOn} icons={icons} />
      </span>
      <span css={styles.left({ isOn })}>
        <LeftIcon isOn={isOn} icons={icons} />
      </span>
    </div>
  )
}

export default Toggle
